import { Divider, VStack } from "@chakra-ui/layout";
import { Button, HStack, Stack, Text, Wrap, WrapItem, useToast } from "@chakra-ui/react";
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import FileThumbnail from "@components/ChakraUI/FileThumbnail";
import SimpleInput from "@components/ChakraUI/SimpleInput";
import SimpleSelect from "@components/ChakraUI/SimpleSelect";
import SimpleTextArea from "@components/ChakraUI/SimpleTextArea";
import TagList, { type TagOption } from "@components/ChakraUI/TagList";
import type { ContactPerson, FileData, StepOneData } from "@models/Order";
import { getAddress } from "@utils/customerUtils";
import LoadingIndicator from "@components/Loading";
import {
	useCustomerAccountData,
	useCustomerCompetences,
	useCustomerUsers,
	useLoggedInUser,
} from "@api/hooks";

const FILE_UPLOAD_ROOT = import.meta.env.VITE_FILE_UPLOAD_ROOT || "https://oo-file.test-1.fruster.se";

interface Props {
	data: StepOneData;
	onChangeData: (data: StepOneData) => void;
	toggleNextButtonDisable: (status: boolean) => void;
	customer?: any;
	loadingCustomerData?: boolean;
}

const StepOne = (
	{ customer, toggleNextButtonDisable, onChangeData, data, loadingCustomerData = false }: Props,
	ref: any
) => {
	const toast = useToast();
	const fileUploadInputRef = useRef<HTMLInputElement>(null);
	const [files, setFiles] = useState<File[]>([]);

	// API hooks
	const loggedInUser = useLoggedInUser();
	const { competences: branches, isLoading: loadingCustomerCompetences } = useCustomerCompetences(customer?.id);
	const { customerUsers, isLoading: loadingCustomerUsers } = useCustomerUsers(customer?.id);
	const { customerAccountUser } = useCustomerAccountData(loggedInUser?.id);

	const [selectedBranch, setSelectedBranch] = useState("");
	const [selectedService, setSelectedService] = useState("");
	const [selectedContactPerson, setSelectedContactPerson] = useState<ContactPerson>({ id: "" });
	const [skills, setSkills] = useState<TagOption[]>([]);
	const [jobDescription, setJobDescription] = useState("");
	const [workPlaceDescription, setWorkPlaceDescription] = useState("");
	const [city, setCity] = useState("");
	const [address, setAddress] = useState("");
	const [zipCode, setZipCode] = useState("");
	const [imageFiles, setImageFiles] = useState<FileData[]>([]);
	const [documentFiles, setDocumentFiles] = useState<FileData[]>([]);
	const [isLoadingFileUpload, setIsLoadingFileUpload] = useState<boolean>(false);
	const [errorMessage, setErrorMessage] = useState<string>("");

	const FileUploadRules = [
		{
			validTypes: ["image/png", "image/jpeg"],
			maxQuantity: 2,
			warningMessage: "Maximalt antal bilder är 2",
			currentQuantity: imageFiles.length ?? 0,
		},
		{
			validTypes: ["application/pdf"],
			maxQuantity: 2,
			warningMessage: "Maximalt antal dokument är 2",
			currentQuantity: documentFiles.length ?? 0,
		},
	];

	const services = useMemo(() => {
		const selectedBranchData = branches?.find((c) => c.key === selectedBranch);
		if (selectedBranchData) {
			return selectedBranchData.items;
		}
		return [];
	}, [selectedBranch, branches]);

	const isDataLoading = loadingCustomerCompetences || loadingCustomerUsers || loadingCustomerData;

	useEffect(() => {
		data.competence.branch && setSelectedBranch(data.competence.branch);
		data.competence.service && setSelectedService(data.competence.service);
		data.contactPersons &&
			data.contactPersons?.length > 0 &&
			data.contactPersons[0]?.id &&
			setSelectedContactPerson(data.contactPersons[0]);
		data.customerDescription && setWorkPlaceDescription(data.customerDescription);
		data.description && setJobDescription(data.description);
		data.address && setAddress(getAddress(data.address));
		data.address?.city && setCity(data.address.city);
		data.address?.postalCode && setZipCode(data.address.postalCode);
		data.documents && setDocumentFiles(data.documents);
		data.images && setImageFiles(data.images);
	}, [data]);

	useImperativeHandle(
		ref,
		() => ({
			onConfirmData() {
				onChangeData({
					address: { address1: address, city, postalCode: zipCode },
					competence: {
						branch: selectedBranch,
						service: selectedService,
						skills: skills.filter((item) => item.isSelected).map((item) => item.key) ?? [],
					},
					customerDescription: workPlaceDescription,
					description: jobDescription,
					contactPersons: [selectedContactPerson],
					documents: documentFiles ?? undefined,
					images: imageFiles ?? undefined,
				});
			},
		}),
		[
			jobDescription,
			selectedContactPerson,
			workPlaceDescription,
			selectedService,
			selectedBranch,
			address,
			zipCode,
			city,
			skills,
			documentFiles,
			imageFiles,
		]
	);

	useEffect(() => {
		if (selectedService && selectedBranch && isValidCompetence()) {
			toggleNextButtonDisable(false);
		} else {
			toggleNextButtonDisable(true);
		}
	}, [selectedService, selectedBranch, skills, toggleNextButtonDisable]);

	useEffect(() => {
		!data.customerDescription && setWorkPlaceDescription(customer?.description ?? "");
		const addressData = customer?.visitingAddress;
		!data.address?.city && setCity(addressData?.city ?? "");
		!data.address?.postalCode && setZipCode(addressData?.postalCode ?? "");
		!getAddress(data.address) && setAddress(addressData ? getAddress(addressData) : "");
	}, [customer?.description, customer?.visitingAddress, data.address, data.customerDescription]);

	useEffect(() => {
		customerAccountUser?.id && !data.contactPersons && setSelectedContactPerson(customerAccountUser as ContactPerson);
	}, [customerAccountUser?.id, data?.contactPersons]);

	useEffect(() => {
		const skillsData = services?.find((s) => s.key === selectedService)?.items ?? [];
		if (data.competence?.skills && data.competence?.skills?.length > 0) {
			setSkills(
				skillsData.map((skill: any) => ({
					key: skill.key,
					value: skill.name.sv as string,
					isSelected: data.competence.skills?.includes(skill.key),
				}))
			);
		} else {
			setSkills(
				skillsData.map((skill: any) => ({
					key: skill.key,
					value: skill.name.sv as string,
					isSelected: false,
				}))
			);
		}
	}, [selectedService, data.competence?.skills, services]);

	useEffect(() => {
		if (files.length > 0) {
			uploadFileData(files);
		}
	}, [files]);

	const uploadFileData = async (files: File[]) => {
		setIsLoadingFileUpload(true);
		try {
			for (const file of files) {
				await handleUpload(file);
			}
		} catch (error) {
			setIsLoadingFileUpload(false);
			console.log({ error });
		}
	};

	const handleUpload = async (file: File) => {
		setIsLoadingFileUpload(true);
		toggleNextButtonDisable(true);
		const formData = new FormData();
		formData.append("file", file);

		await fetch(`${FILE_UPLOAD_ROOT}/upload?path=/${loggedInUser.profile.organisation.id}`, {
			method: "POST",
			body: formData,
		})
			.then((response) => {
				if (!response.ok) {
					console.error(response);
					throw new Error("Network response was not ok");
				}
				return response.json();
			})
			.then((data) => {
				const { amazonUrl, url, originalName, mimeType } = data.data;
				if (mimeType === "application/pdf") {
					setDocumentFiles((prev) => [...prev, { url: url ?? amazonUrl, name: originalName }]);
				} else {
					setImageFiles((prev) => [...prev, { url: url ?? amazonUrl, name: originalName }]);
				}
			})
			.catch((error) => {
				toast({
					title: "Det uppstod ett problem med filuppladdningen",
					status: "error",
					duration: 9000,
					isClosable: true,
					variant: "subtle",
				});
				console.error("There was a problem with file upload:", error);
			})
			.finally(() => {
				setFiles([]);
				setIsLoadingFileUpload(false);
				toggleNextButtonDisable(false);
			});
	};

	const showSkills = useMemo(() => {
		if (selectedBranch && selectedService) {
			const selectedBranchData = branches?.find((c) => c.key === selectedBranch);
			if (selectedBranchData) {
				const selectedServiceData = selectedBranchData.items?.find((c: any) => c.key === selectedService);
				return !!selectedServiceData?.items?.length;
			}
		}
		return false;
	}, [selectedBranch, selectedService, branches]);

	const isValidCompetence = (): boolean => {
		if (showSkills) {
			const selectedSkills = skills.filter((s) => s.isSelected);
			if (selectedSkills.length >= 1 && selectedSkills.length <= 5) {
				return true;
			}
			return false;
		} else {
			return true;
		}
	};

	const handleSkillSelect = (itemKey: string) => {
		setSkills((prev) => {
			const skillList = [...prev];
			const itemIndex = prev.findIndex((s) => s.key === itemKey);
			if (itemIndex >= 0) {
				const changedItem = { ...prev[itemIndex] };
				changedItem.isSelected = changedItem.isSelected ? false : true;
				skillList.splice(itemIndex, 1, changedItem);
			}
			return skillList;
		});
	};

	const getContactPersons = () => {
		return (
			customerUsers?.users.map((user: any) => ({
				key: user.id,
				value:
					loggedInUser.id === user.id
						? `${user.firstName} ${user.lastName} (Jag)`
						: `${user.firstName} ${user.lastName}`,
			})) ?? []
		);
	};

	const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
		if (event.target.files && event.target.files.length > 0) {
			const selectedFiles = Array.from(event.target.files);
			// check the validation
			const uploadedFileRules = FileUploadRules.map((u) => ({
				...u,
				toBeUploadedFileCount: selectedFiles.filter((f) => u.validTypes.includes(f.type)).length,
			}));
			const violatedFileRule = uploadedFileRules.find(
				(ufr) => ufr.toBeUploadedFileCount + ufr.currentQuantity > ufr.maxQuantity
			);
			if (!!violatedFileRule) {
				setErrorMessage(violatedFileRule.warningMessage);
				return;
			}
			setFiles((prev) => [...prev, ...selectedFiles]);
			setErrorMessage("");
		}
	};

	const fileRemoveHandler = (fileUrl: string) => {
		if (imageFiles.find((f) => f.url === fileUrl)) {
			setImageFiles((prev) => prev.filter((fi) => fi.url !== fileUrl));
		} else {
			setDocumentFiles((prev) => prev.filter((df) => df.url !== fileUrl));
		}
	};

	return (
		<VStack spacing="12" alignItems={"start"}>
			{/* Start of Beskriv kompetensen du efterfrågar */}
			<VStack alignItems="start" w="full" spacing="7">
				<VStack alignItems="start" w="full" spacing="1">
					<Text fontWeight={600} fontSize={16} color={"#353535"} opacity={0.5} m="0">
						Beskriv kompetensen du efterfrågar
					</Text>
					<Divider mt={2} />
				</VStack>
				{!isDataLoading && (
					<>
						<Stack direction={{ base: "column", lg: "row" }}>
							<SimpleSelect
								label="Välj bransch"
								value={selectedBranch}
								onChange={(e) => setSelectedBranch(e.target.value)}
								options={
									branches?.map((item) => ({
										key: item.key,
										value: item.name.sv as string,
									})) ?? []
								}
							/>
							<SimpleSelect
								label="Välj tjänst"
								value={selectedService}
								onChange={(e) => setSelectedService(e.target.value)}
								options={
									services?.map((item: any) => ({
										key: item.key,
										value: item.name.sv as string,
									})) ?? []
								}
							/>
						</Stack>
						{showSkills && (
							<TagList
								tagList={skills}
								disabled={skills.length === 0}
								label="Kompetenskrav"
								userGuideText="Välj minst en eller max fem egenskaper inom vald bransch"
								onClick={(item) => handleSkillSelect(item)}
							/>
						)}
					</>
				)}
			</VStack>

			{/* End of Beskriv kompetensen du efterfrågar */}
			{/* Start of Beskriv uppdraget */}
			{!isDataLoading && (
				<>
					<VStack alignItems="start" spacing="7" w="full">
						<VStack alignItems="start" w="full" spacing="3">
							<Text fontWeight={600} fontSize={16} color={"#353535"} opacity={0.5} m="0">
								Beskriv uppdraget
							</Text>
							<Divider />
						</VStack>
						<Stack direction={{ base: "column", xl: "row" }} w="full">
							<SimpleTextArea
								width={450}
								labelFontSize="14px"
								labelFontWeight={700}
								label="Arbetsbeskrivning"
								value={jobDescription}
								onChange={(e) => setJobDescription(e.target.value)}
								placeHolder="Eventuell mer information om uppdraget, t.ex. vad som ska göras, vem man vikarierar för och annan praktisk information."
							/>
							<SimpleTextArea
								width={450}
								labelFontSize="14px"
								labelFontWeight={700}
								label="Om arbetsplatsen"
								value={workPlaceDescription}
								onChange={(e) => setWorkPlaceDescription(e.target.value)}
							/>
						</Stack>
						<VStack alignItems="start" mr={5} minWidth={"100%"}>
							<HStack justifyContent="space-between" align={"baseline"} width={"100%"}>
								<Text fontSize="14px" fontWeight={700} mt={4} mb={0}>
									Dokument till uppdraget
								</Text>
								<input
									style={{ display: "none" }}
									type="file"
									multiple
									onChange={handleFileUpload}
									ref={fileUploadInputRef}
									accept={"image/jpeg,image/png,application/pdf"}
								/>
								<Button
									mr={5}
									fontSize="14px"
									fontWeight={500}
									color={"#219CD0"}
									variant="link"
									onClick={() => fileUploadInputRef.current?.click()}
									isLoading={isLoadingFileUpload}
									spinner={<LoadingIndicator size={"s"} />}
								>
									+ Ladda upp
								</Button>
							</HStack>
							{(![...documentFiles, ...imageFiles] || [...documentFiles, ...imageFiles].length === 0) &&
								!isLoadingFileUpload &&
								!errorMessage && (
									<Text fontSize="16px" fontWeight={400} m={0} color={"#999999"}>
										Inga dokument uppladdade
									</Text>
								)}
							{isLoadingFileUpload && <LoadingIndicator size={"s"} />}
							<Wrap align="center" mr={5}>
								{[...documentFiles, ...imageFiles].map((file, index) => (
									<WrapItem key={index}>
										<FileThumbnail onRemove={fileRemoveHandler} fileName={file.name} fileUrl={file.url} />
									</WrapItem>
								))}
							</Wrap>
							<Text fontWeight={500} color={"#EB5757"} fontSize={"12px"} m="0">
								{errorMessage}
							</Text>
						</VStack>
					</VStack>
					{/* End of Beskriv uppdraget */}
					<VStack alignItems="start" spacing="7" pb="4" w="full">
						<VStack alignItems="start" w="full" spacing="3">
							<Text fontWeight={600} fontSize={16} color={"#353535"} opacity={0.5} m="0">
								Kontakt och plats
							</Text>
							<Divider />
						</VStack>

						<Stack direction={{ base: "column", lg: "row" }}>
							<SimpleSelect
								label="Kontaktperson för uppdraget"
								value={selectedContactPerson.id}
								onChange={(e) => {
									const selectedPerson =
										customerUsers?.users && customerUsers.users.find((c: any) => c.id === e.target.value);
									!!selectedPerson && setSelectedContactPerson(selectedPerson);
								}}
								options={getContactPersons()}
							/>
							<SimpleInput
								width={300}
								label="Telefonnummer till kontaktperson"
								value={selectedContactPerson?.profile?.phoneNumber || ""}
								onChange={(e) =>
									setSelectedContactPerson((prev) => ({
										...prev,
										profile: { ...prev.profile, phoneNumber: e.target.value },
									}))
								}
							/>
						</Stack>

						<Stack direction={{ base: "column", xl: "row" }} alignItems="start" mr={5} minWidth={"100%"} spacing="6">
							<SimpleInput width={300} label="Adress" value={address} onChange={(e) => setAddress(e.target.value)} />
							<SimpleInput
								width={300}
								label="Postnummer"
								value={zipCode}
								onChange={(e) => setZipCode(e.target.value)}
							/>
							<SimpleInput width={300} label="Ort" value={city} onChange={(e) => setCity(e.target.value)} />
						</Stack>
					</VStack>
				</>
			)}
			{isDataLoading && <LoadingIndicator size={"s"} />}
		</VStack>
	);
};

export default forwardRef(StepOne);
